import { v4 as uuidv4 } from "uuid";
import { motion } from "framer-motion";

// Icons
import { ArrowDownIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import AdvisoryDomainImg1 from "../assets/images/advisory-domain-image1.jpg";
import AdvisoryDomainImg2 from "../assets/images/advisory-domain-image2.jpg";
import AdvisoryDomainImg3 from "../assets/images/advisory-domain-image3.png";
import AdvisoryDomainImg4 from "../assets/images/advisory-domain-image4.jpg";
import AdvisoryDomainImg5 from "../assets/images/advisory-domain-image5.jpg";
import AdvisoryDomainImg6 from "../assets/images/advisory-domain-image6.png";

const container = {
  hidden: { opacity: 1, scale: 1 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.2,
      duration: 0.25,
    },
  },
};

const item = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
  },
};

const AdvisoryServices = () => {
  const advisoryOfferings = [
    {
      // icon: AdvisoryIcon1,
      label: "Top-down, Bottom-up.",
      description:
        "We connect leadership vision with frontline realities to craft strategies that reflect how your organization really works.",
    },
    {
      // icon: AdvisoryIcon2,
      label: "Grounded in Deep Expertise.",
      description:
        "We bring senior advisors and engineers to every engagement, driving strategic decisions while remaining technically grounded.",
    },
    {
      // icon: AdvisoryIcon3,
      label: "Action Alongside Insight.",
      description:
        "We mobilize teams to act, aligning people and operating models to turn strategy into coordinated execution that doesn’t stall.",
    },
    {
      // icon: AdvisoryIcon4,
      label: "Built Together, Built to Last.",
      description:
        "We co-create with your teams, ensuring every strategy is tailored to your unique environment and needs.",
    },
  ];

  const advisoryExamples = [
    {
      project: "Cloud Value Realization (FinOps)",
    },
    {
      project: "Technology Strategy",
    },
    {
      project: "Reliability and Resiliency Roadmap",
    },
    {
      project: "Cloud Portfolio Assessment",
    },
    {
      project: "Digital Portal Assessment & Strategy",
    },
    {
      project: "FinTech Re-architecture",
    },
  ];

  return (
    <main className="text-center flex flex-col gap-6 md:gap-28 px-6 py-6 md:px-12 md:py-12">
      <div className="relative flex justify-center items-center h-[500px]">
        <motion.div
          variants={container}
          initial="hidden"
          animate="visible"
          className="flex flex-col gap-4 md:gap-8 mx-auto px-2.5 sm:px-0 text-center max-w-3xl"
        >
          <motion.h1
            variants={item}
            className="text-6xl font-bold leading-[1.15] text-black sm:leading-[1.15]"
          >
            <span className=" text-purple-700">RAVL</span> Advisory
          </motion.h1>
          <motion.p variants={item} className="text-gray-700 sm:text-xl">
            A strategy is only valuable if it’s implemented. It should move you
            forward, become the momentum that transforms vision into action, and
            deliver real impact across the organization. RAVL combines deep FSI
            fluency and business insight with engineering expertise to deliver
            bespoke solutions built for real-world execution.
          </motion.p>
          <motion.div variants={item} className="flex flex-wrap justify-center">
            <a
              href="#advisory-offerings"
              className="btn-primary flex items-center justify-center shadow-md text-base w-3/4 md:w-[45%] gap-4"
            >
              Explore Below <ArrowDownIcon className="h-4" />
            </a>
          </motion.div>
        </motion.div>
      </div>

      {/* Offerings Section */}
      <section
        id="advisory-offerings"
        className="grid grid-cols-1 gap-11 xl:px-32 pt-20 md:pt-[88px]"
      >
        <h2 className="text-2xl md:text-4xl font-bold text-gray-900 ">
          Our <span className="text-purple-700">Advisory</span> services bring
          together top
          <span className="text-purple-700"> strategic</span> and
          <span className="text-purple-700"> engineering minds</span> to solve
          your
          <span className="text-purple-700"> biggest challenges</span>
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 bg-gradient-to-r from-[#4a0099] via-[#2d006e] to-[#1a0044] rounded-xl overflow-hidden w-full">
          {advisoryOfferings.map((offering, index) => (
            <div
              key={index}
              className="flex flex-col items-center text-center p-8 gap-5 border-r border-white/20 last:border-r-0"
            >
              {/* <div className="flex h-28 w-28 flex-shrink-0 items-center justify-center rounded-full bg-white p-5">
                <img
                  src={offering.icon}
                  alt={offering.label}
                  className="h-full w-full object-contain"
                />
              </div> */}
              <h3 className="text-white font-bold text-xl leading-snug">
                {offering.label}
              </h3>
              <p className="text-white/80 text-sm leading-relaxed">
                {offering.description}
              </p>
            </div>
          ))}
        </div>
      </section>
      <div className="flex flex-col items-center gap-8">
        <p className="text-base md:text-xl">
          Connect strategy to delivery. Explore how RAVL Build makes things
          real.
        </p>
        <Link
          to={"/services/build"}
          className="btn-primary flex items-center justify-center shadow-md text-base w-[15rem] md:w-[20rem]"
        >
          Explore Build
        </Link>
      </div>
      {/* Examples Section */}
      <section className="grid grid-cols-1 gap-11 pt-20 md:pt-[88px]">
        <h2 className="text-4xl font-bold text-gray-900 sm:text-5xl">
          <span className="text-purple-700">Examples</span> Of Our{" "}
          <span className="text-purple-700">Advisory </span>
          Work
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {/* Row 1: icon1, photo1, icon2, photo3, photo4 */}
          {/* icon card */}
          <div className="rounded-2xl overflow-hidden bg-gradient-to-b from-[#180043] to-[#080015] flex flex-col items-center justify-center gap-2 p-6 h-56">
            <p className="text-white font-bold text-lg text-center leading-snug">
              {advisoryExamples[0].project}
            </p>
          </div>
          {/* photo card */}
          <div className="rounded-2xl overflow-hidden h-56">
            <img
              src={AdvisoryDomainImg1}
              className="w-full h-full object-cover"
            />
          </div>
          {/* icon card */}
          <div className="rounded-2xl overflow-hidden bg-gradient-to-b from-[#180043] to-[#080015] flex flex-col items-center justify-center gap-2 p-6 h-56">
            <p className="text-white font-bold text-lg text-center leading-snug">
              {advisoryExamples[1].project}
            </p>
          </div>
          {/* photo card */}
          <div className="rounded-2xl overflow-hidden h-56">
            <img
              src={AdvisoryDomainImg2}
              className="w-full h-full object-cover"
            />
          </div>
          {/* icon card */}
          <div className="rounded-2xl overflow-hidden bg-gradient-to-b from-[#180043] to-[#080015] flex flex-col items-center justify-center gap-2 p-6 h-56">
            <p className="text-white font-bold text-lg text-center leading-snug">
              {advisoryExamples[2].project}
            </p>
          </div>
          {/* photo card */}
          <div className="rounded-2xl overflow-hidden h-56">
            <img
              src={AdvisoryDomainImg3}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Row 2: photo, icon1, photo2, icon3, icon4 */}
          {/* photo card */}
          <div className="rounded-2xl overflow-hidden h-56">
            <img
              src={AdvisoryDomainImg4}
              className="w-full h-full object-cover"
            />
          </div>
          {/* icon card */}
          <div className="rounded-2xl overflow-hidden bg-gradient-to-b from-[#180043] to-[#080015] flex flex-col items-center justify-center gap-2 p-6 h-56">
            <p className="text-white font-bold text-lg text-center leading-snug">
              {advisoryExamples[3].project}
            </p>
          </div>
          {/* photo card */}
          <div className="rounded-2xl overflow-hidden h-56">
            <img
              src={AdvisoryDomainImg5}
              className="w-full h-full object-cover"
            />
          </div>
          {/* icon card */}
          <div className="rounded-2xl overflow-hidden bg-gradient-to-b from-[#180043] to-[#080015] flex flex-col items-center justify-center gap-2 p-6 h-56">
            <p className="text-white font-bold text-lg text-center leading-snug">
              {advisoryExamples[4].project}
            </p>
          </div>
          {/* photo card */}
          <div className="rounded-2xl overflow-hidden h-56">
            <img
              src={AdvisoryDomainImg6}
              className="w-full h-full object-cover"
            />
          </div>
          {/* icon card */}
          <div className="rounded-2xl overflow-hidden bg-gradient-to-b from-[#180043] to-[#080015] flex flex-col items-center justify-center gap-2 p-6 h-56">
            <p className="text-white font-bold text-lg text-center leading-snug">
              {advisoryExamples[5].project}
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AdvisoryServices;
